import { useMeeting, useParticipant, usePubSub } from "@videosdk.live/react-sdk";
import { useEffect, useRef } from "react";
import { useAppContext } from "../AppContextDef";
import usePubsubFunction from "../utils/usePubsubFunction";
import { mergeDeviceInfo } from "../utils/participantStats";
import useIdentityLog from "../hooks/useIdentityLog";
import { EXECUTED_BY } from "../utils/identityLog";

// Recorder-side network watchdog: if the customer's INCOMING video bitrate stays
// below this for LOW_BITRATE_STREAK consecutive 1s samples, we treat the network
// as unstable. Lives here (always mounted on the recorder) so the verdict
// survives page transitions instead of remounting per page section.
const LOW_BITRATE_KBPS = 30;
const LOW_BITRATE_STREAK = 5;

const PubSubHandler = () => {
  const {
    setAllPubsubData,
    setCurrentPageData,
    setFaceVerified,
    setAgreedData,
    setFaceDetectError,
    setMediaPermissionError,
    setIsAgreeButtonClick,
    setIsDisagreeButtonClick,
    setIsTimerRunningData,
    setJourneyConfigData,
    setJourneyPubsubData,
    setTimerData,
    setRunningTimeData,
    setSelectedLang,
    setIsVideoConsentStart,
    setOpenVideoErr,
    setOpenVideoFailErr,
    setIsAbortSignal,
    setIsJourneyEnded,
    setOpenDeviceChange,
    setOpenVideoFreeze,
    setOpenErrorBox,
    setOpenTextMatchErr,
    setAccuracy,
    setOpenJourneyLimitBox,
    setQnaPubSubData,
    setTerminationFlag,
    setDelegatedApiSuccess,
    isMeetingEnded,
    currentPageData,
    isTimerRunningData,
    setIsRecorderNetworkUnstable,
    setDeviceInfo,
    setCpuStatus,
    setNetworkStatus,
  } = useAppContext();

  // Writes the IDENTITY rows for calls the recorder delegated to the client
  // (see the IDENTITY_RESULT subscription below).
  const logIdentityCall = useIdentityLog();

  // ---- recorder network watchdog -----------------------------------------
  // Watch the customer's INCOMING video bitrate continuously. If it stays at ~0
  // for LOW_BITRATE_STREAK consecutive seconds, set our own context flag (drives
  // the recorder dialog) and publish NETWORK_UNSTABLE so the client mirrors it
  // (dialog + consent stop/disable). On recovery we clear and publish false.
  // We also watch the inbound freezeCount: a NEW freeze while on the VideoConsent
  // screen publishes VIDEO_FREEZE so the client can restart an in-progress
  // consent recording (frames arriving but stalling — a bitrate check can miss it).
  const pubsubPublishMethod = usePubsubFunction();
  const { publish: networkUnstablePublish } = usePubSub(`NETWORK_UNSTABLE`);
  const { publish: videoFreezePublish } = usePubSub(`VIDEO_FREEZE`);
  const { participants } = useMeeting({});
  const nonLocalParticipantId = Array.from(participants.values()).find(
    (p) => !p.local
  )?.id;
  const { getVideoStats, webcamOn } = useParticipant(nonLocalParticipantId);

  const recorderLowBitrateCountRef = useRef(0);
  const recorderUnstableShownRef = useRef(false);
  // freezeCount from getVideoStats is CUMULATIVE for the whole session, so it
  // can't be compared to a fixed value. We snapshot it when a consent recording
  // starts (the baseline) and treat any later increase as a freeze that happened
  // *during* this recording. Reset to null between recordings so each attempt
  // re-baselines; a drop below the baseline (track renegotiated → counter reset)
  // re-baselines too, so we never miss post-reset freezes or fire false ones.
  const freezeBaselineRef = useRef(null);
  // Fresh values for the interval closure (which is created once on mount and
  // never restarts on page/recording changes).
  const currentPageDataRef = useRef(currentPageData);
  const isRecordingRef = useRef(!!isTimerRunningData?.isRunning);
  useEffect(() => {
    currentPageDataRef.current = currentPageData;
  }, [currentPageData]);
  useEffect(() => {
    isRecordingRef.current = !!isTimerRunningData?.isRunning;
  }, [isTimerRunningData]);

  useEffect(() => {
    const setUnstable = (isUnstable) => {
      setIsRecorderNetworkUnstable(isUnstable);
      pubsubPublishMethod(
        networkUnstablePublish,
        "NetworkUnstable",
        { persist: true },
        { isUnstable }
      );
    };

    if (!webcamOn || !getVideoStats || !nonLocalParticipantId || isMeetingEnded) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const stats = await getVideoStats();
        const s = stats && stats.length ? stats[0] : undefined;
        const bitrate = s?.bitrate ?? 0;
        const freezeCount = s?.freezeCount ?? 0;
        console.log(
          `[Recorder] customer incoming video bitrate: ${bitrate} (low if < ${LOW_BITRATE_KBPS}), freezeCount: ${freezeCount}`
        );

        // Freeze detection — only while a consent recording is actually in
        // progress. freezeCount is cumulative, so we baseline it on the first
        // in-recording tick and treat a later increase as a freeze DURING this
        // recording, asking the client to restart consent.
        if (
          currentPageDataRef.current === "VideoConsent" &&
          isRecordingRef.current
        ) {
          if (
            freezeBaselineRef.current === null ||
            freezeCount < freezeBaselineRef.current
          ) {
            // first tick of this recording, or the counter reset → (re)baseline
            freezeBaselineRef.current = freezeCount;
          } else if (freezeCount > freezeBaselineRef.current) {
            console.log(
              `%c ❄ FREEZE %c +${freezeCount - freezeBaselineRef.current} %c freezeCount ${freezeBaselineRef.current} → ${freezeCount} — requesting consent restart`,
              "background:#0e7490;color:#fff;font-weight:bold;border-radius:3px 0 0 3px;padding:2px 4px",
              "background:#f59e0b;color:#1f2937;font-weight:bold;padding:2px 4px",
              "color:#0e7490;font-weight:bold;padding-left:6px"
            );
            pubsubPublishMethod(
              videoFreezePublish,
              "VideoFreeze",
              { persist: false },
              { frozen: true, freezeCount, timestamp: Date.now() }
            );
            // advance the baseline so we don't re-fire every tick for the same
            // accumulated count (the client restarts on the first event anyway)
            freezeBaselineRef.current = freezeCount;
          }
        } else {
          // not in an active consent recording — drop any stale baseline
          freezeBaselineRef.current = null;
        }

        if (bitrate < LOW_BITRATE_KBPS) {
          if (recorderLowBitrateCountRef.current >= LOW_BITRATE_STREAK) {
            recorderLowBitrateCountRef.current = 0;
          }
          recorderLowBitrateCountRef.current += 1;
          if (
            recorderLowBitrateCountRef.current >= LOW_BITRATE_STREAK &&
            !recorderUnstableShownRef.current
          ) {
            recorderUnstableShownRef.current = true;
            console.warn(
              "[Recorder] customer video bitrate low for 5s — flagging network unstable"
            );
            setUnstable(true);
          }
        } else if (
          recorderLowBitrateCountRef.current > 0 ||
          recorderUnstableShownRef.current
        ) {
          recorderLowBitrateCountRef.current = 0;
          if (recorderUnstableShownRef.current) {
            recorderUnstableShownRef.current = false;
            console.log("[Recorder] customer video bitrate recovered");
            setUnstable(false);
          }
        }
      } catch (err) {
        console.error("[Recorder] failed to read customer video stats", err);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      recorderLowBitrateCountRef.current = 0;
      freezeBaselineRef.current = null;
      if (recorderUnstableShownRef.current) {
        recorderUnstableShownRef.current = false;
        setUnstable(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamOn, getVideoStats, nonLocalParticipantId, isMeetingEnded]);

  usePubSub("CURRENT_PAGE", {
    onMessageReceived: ({ payload }) => {
      setCurrentPageData(payload?.message);
      // if (handlePageDataFromChild) {
      //   handlePageDataFromChild(message?.message);
      // }
      setFaceVerified(false);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setCurrentPageData(latestMessage.payload?.message);
      }
    },
  });

  usePubSub("SELECT_LANG", {
    onMessageReceived: ({ message }) => {
      setSelectedLang({ lang: message, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setSelectedLang({ lang: latestMessage.message, timestamp: Date.now() });
      }
    },
  });

  usePubSub("ALL_DETAILS", {
    onMessageReceived: ({ payload }) => {
      setAllPubsubData((prev) => {
        return {
          ...(prev || {}),
          ...payload.message,
          qna: payload?.qna,
          journeyId: payload?.journeyId,
        };
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setAllPubsubData({
          ...latestMessage.payload?.message,
          qna: latestMessage.payload?.qna,
          journeyId: latestMessage.payload?.journeyId,
        });
      }
    },
  });

  // Delegated face-verification calls run on the CLIENT, but the recorder is the
  // sole writer of IDENTITY rows, so the client forwards each answered call here
  // and this peer builds and POSTs the row. Carries every attempt, not just the
  // success that DELEGATED_API_SUCCESS reports.
  //
  // No onOldMessagesReceived: the channel is published unpersisted precisely
  // because every message is a database write, and replaying history after a
  // recorder reload would duplicate rows.
  usePubSub("IDENTITY_RESULT", {
    onMessageReceived: ({ payload }) => {
      logIdentityCall({
        apiName: payload?.apiName,
        body: payload?.body,
        page: payload?.page,
        executedBy: EXECUTED_BY.CLIENT,
        delegated: true,
      });
    },
  });

  usePubSub("JOURNEY_CONFIG_DATA", {
    onMessageReceived: ({ payload }) => {
      setJourneyConfigData(payload);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setJourneyConfigData(latestMessage.payload);
      }
    },
  });

  usePubSub("JOURNEY_DATA", {
    onMessageReceived: ({ payload }) => {
      setJourneyPubsubData(payload);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setJourneyPubsubData(latestMessage.payload);
      }
    },
  });

  usePubSub("MEDIA_ERROR", {
    onMessageReceived: ({ payload }) => {
      setMediaPermissionError(payload.err);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setMediaPermissionError(latestMessage.payload.err);
      }
    },
  });

  usePubSub("FACE_DETECT_ERROR", {
    onMessageReceived: ({ payload }) => {
      setFaceDetectError({ err: payload.err, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setFaceDetectError({
          err: latestMessage.payload.err,
          timestamp: Date.now(),
        });
      }
    },
  });

  usePubSub("FACE_VERIFIED", {
    onMessageReceived: ({ payload }) => {
      setFaceVerified({
        isVerified: payload.isVerified,
        timestamp: Date.now(),
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setFaceVerified({
          isVerified: latestMessage.payload.isVerified,
          timestamp: Date.now(),
        });
      }
    },
  });

  usePubSub("AGREED", {
    onMessageReceived: ({ payload }) => {
      setAgreedData(payload.isAgreed);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setAgreedData(latestMessage.payload.isAgreed);
      }
    },
  });

  usePubSub("START_VIDEO", {
    onMessageReceived: ({ payload }) => {
      setIsVideoConsentStart(payload.isStart);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsVideoConsentStart(latestMessage.payload.isStart);
      }
    },
  });

  usePubSub("AGREE", {
    onMessageReceived: ({ payload }) => {
      setIsAgreeButtonClick({
        isAgreeClick: payload.isAgreeClick,
        currentPage: payload.currentPage,
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsAgreeButtonClick({
          isAgreeClick: latestMessage.payload.isAgreeClick,
          currentPage: latestMessage.payload.currentPage,
        });
      }
    },
  });

  usePubSub("DISAGREE", {
    onMessageReceived: ({ payload }) => {
      setIsDisagreeButtonClick({
        isDisagreeClick: payload.isDisagreeClick,
        currentPage: payload.currentPage,
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsDisagreeButtonClick({
          isDisagreeClick: latestMessage.payload.isDisagreeClick,
          currentPage: latestMessage.payload.currentPage,
        });
      }
    },
  });

  usePubSub("TIMER_RUNNING", {
    onMessageReceived: ({ payload }) => {
      setIsTimerRunningData({
        isRunning: payload.isRunning,
        timestamp: new Date(),
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsTimerRunningData({
          isRunning: latestMessage.payload.isRunning,
          timestamp: new Date(),
        });
      }
    },
  });


  usePubSub("RUNNING_TIME_DATA", {
    onMessageReceived: ({ message }) => {
      setRunningTimeData(message);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setRunningTimeData(latestMessage.message);
      }
    },
  });

  usePubSub("TIMER_DATA", {
    onMessageReceived: ({ payload }) => {
      setTimerData(payload.time);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setTimerData(latestMessage.payload.time);
      }
    },
  });


  usePubSub("VIDEO_RECORDING_ATTEMPT_ERR", {
    onMessageReceived: ({ payload }) => {
      setOpenVideoErr(payload.open);
      setAccuracy(payload.accuracy)
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenVideoErr(latestMessage.payload.open);
      }
    },
  });

  usePubSub("TEXT_MATCHING_FAILED", {
    onMessageReceived: ({ payload }) => {
      setOpenTextMatchErr(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenTextMatchErr(latestMessage.payload.open);
      }
    },
  })

  usePubSub("VIDEO_RECORDING_ATTEMPT_FAIL_ERR", {
    onMessageReceived: ({ payload }) => {
      setOpenVideoFailErr(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenVideoFailErr(latestMessage.payload.open);
      }
    },
  });

  usePubSub("DEVICE_CHANGED", {
    onMessageReceived: ({ payload }) => {
      setOpenDeviceChange(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenDeviceChange(latestMessage.payload.open);
      }
    },
  });

  usePubSub("VIDEO_FREEZE_DIALOG", {
    onMessageReceived: ({ payload }) => {
      setOpenVideoFreeze(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenVideoFreeze(latestMessage.payload.open);
      }
    },
  });

  usePubSub("ERROR_MEETING_LEFT", {
    onMessageReceived: ({ payload }) => {
      setOpenErrorBox(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenErrorBox(latestMessage.payload.open);
      }
    },
  });

  usePubSub("JOURNEY_LIMIT", {
    onMessageReceived: ({ payload }) => {
      setOpenJourneyLimitBox(payload.showModal);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenJourneyLimitBox(latestMessage.payload.showModal);
      }
    },
  });



  usePubSub("ABORT_SIGNAL", {
    onMessageReceived: ({ payload }) => {
      setIsAbortSignal(payload.isAbort);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsAbortSignal(latestMessage.payload.isAbort);
      }
    },
  });

  usePubSub("AUDIO_PRELOADED", {
    onMessageReceived: ({ message }) => {
    },
    onOldMessagesReceived: (messages) => {
    },
  });

  usePubSub("PRELOADED_AUDIO_SPOKEN", {
    onMessageReceived: ({ message }) => {
    },
    onOldMessagesReceived: (messages) => {
    },
  });

  usePubSub("END_JOURNEY_API_WAITING", {
    onMessageReceived: ({ payload }) => {
      setIsJourneyEnded(payload.isJourneyEnd);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsJourneyEnded(latestMessage.payload.isJourneyEnd);
      }
    },
  });

  usePubSub("TERMINATION_STATUS", {
    onMessageReceived: ({ payload }) => {
      setTerminationFlag(payload.showTerminationMessage === true);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setTerminationFlag(latestMessage.payload.showTerminationMessage === true);
      }
    },
  });

  // No onOldMessagesReceived, and the channel is published unpersisted: a
  // handback CLEARS a verification check, so replaying an old success to a
  // freshly mounted recorder would clear that check on a page the client never
  // ran it for. handback re-checks the page as well, so this is belt and
  // braces — but the message should not survive its step in the first place.
  usePubSub("DELEGATED_API_SUCCESS", {
    onMessageReceived: ({ payload }) => {
      setDelegatedApiSuccess({ ...payload, timestamp: Date.now() });
    },
  });

  usePubSub("QNA_DATA", {
    onMessageReceived: ({ payload }) => {
      setQnaPubSubData(payload);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setQnaPubSubData(latestMessage.payload);
      }
    },
  });

  usePubSub("DEVICE_INFO", {
    onMessageReceived: ({ payload }) => {
      setDeviceInfo((prev) => mergeDeviceInfo(prev, payload));
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) return -1;
        if (a.timestamp < b.timestamp) return 1;
        return 0;
      })[0];
      if (latestMessage) {
        setDeviceInfo((prev) => mergeDeviceInfo(prev, latestMessage.payload));
      }
    },
  });

  usePubSub("QUALITY_LIMITATION", {
    onMessageReceived: ({ payload }) => {
      setCpuStatus(payload.cpuStatus);
      setNetworkStatus(payload.networkStatus);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) return -1;
        if (a.timestamp < b.timestamp) return 1;
        return 0;
      })[0];
      if (latestMessage) {
        setCpuStatus(latestMessage.payload.cpuStatus);
        setNetworkStatus(latestMessage.payload.networkStatus);
      }
    },
  });

  return <></>;
};

export default PubSubHandler;
